import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  CgChevronDown,
  CgFileDocument,
  CgMail,
  CgMathPlus,
  CgSoftwareDownload,
} from 'react-icons/cg';
import {
  Button as ChakraButton,
  Flex,
  Menu,
  Portal,
  Spinner,
  Stack,
} from '@chakra-ui/react';

import { useOpenClose, UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { buildIncludeParams } from '~/core/shared/api';
import { useSelectCategories } from '~/core/shared/api/categories';
import {
  useApplicationDocsTab,
  useDeleteApplicationDoc,
  useUpdateApplicationDoc,
} from '~/core/shared/api/middle-office/application';
import { useApplication } from '~/core/shared/api/middle-office/application/hooks/useApplication/useApplication';
import { useDownloadApplicationDocs } from '~/core/shared/api/middle-office/application/hooks/useDownloadApplicationDocs/useDownloadApplicationDocs';
import {
  CategoryParent,
  DocStatusEnum,
  EMPTY_ARR,
} from '~/core/shared/constants';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useRequiredParams } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Doc, Option, OptionGroup } from '~/core/shared/types';
import { handleSuccess } from '~/core/shared/utils/admin';
import { categoryToDropdown, isDeniedHook } from '~/core/shared/utils/general';
import {
  createFilterParams,
  createSortFilter,
} from '~/core/shared/utils/queries';
import { useDefinition } from '../../../contexts';
import { Button } from '../../Button.component';
import {
  DocCreateModal,
  DocRejectedModal,
  DocsRequestModal,
  DocsTabTypeTable,
  DocUploadModal,
} from '../../docs';
import { DocsRequestRequiredModal } from '../../docs/DocsRequestRequiredModal.component';
import { ApplicationContext } from '../Layout.component';

export type DocsTabContextType = {
  docNeedsToBeUploaded: Doc;
  docNeedsToBeRejected: Doc;
  docTypes: Array<OptionGroup<string>>;
  selectedDocs: Doc[];
  setDocNeedsToBeUploaded: Dispatch<SetStateAction<Doc>>;
  setDocNeedsToBeRejected: Dispatch<SetStateAction<Doc>>;
  setSelectedDocs: Dispatch<SetStateAction<Doc[]>>;
  docCreateModalApi: UseOpenCloseReturn;
  docsRequestModalApi: UseOpenCloseReturn;
  docUploadModalApi: UseOpenCloseReturn;
  docRejectedModalApi: UseOpenCloseReturn;
  docsRequestRequiredModalApi: UseOpenCloseReturn;
  uuid: string;
  definition: string;
  documentOwnerOptions: Array<OptionGroup<string>>;
};

type DocsTabProps = {
  documentTypeCategory?: string[];
  includes?: string[];
};

export const DocsTabContext = createContext<DocsTabContextType>(
  {} as DocsTabContextType,
);

const ownerParams = buildIncludeParams([
  'directors',
  'individual_trustees',
  'sole_trader',
  'shareholders',
  'shareholder_individuals',
  'shareholder_organisations',
  'corporate_trustee_directors',
  'individuals',
  'partners',
  'individual_guarantors',
  'company_guarantors',
  'applicants',
  'asset',
  'additional_assets',
]);

const formatDocumentOwnerOptions = (
  data: Record<any, any>,
): OptionGroup<string>[] => {
  const fields = [
    {
      key: 'applicants',
      label: 'Consumer loan applicants',
    },
    {
      key: 'soleTrader',
      label: 'Sole Trader',
    },
    {
      key: 'individuals',
      label: 'Individuals',
    },
    {
      key: 'partners',
      label: 'Partners',
    },
    {
      key: 'directors',
      label: 'Directors',
    },
    {
      key: 'shareholders',
      label: 'Shareholders',
    },
    {
      key: 'shareholderIndividuals',
      label: 'Shareholder Individuals',
    },
    {
      key: 'shareholderOrganisations',
      label: 'Shareholder Organisations',
    },
    {
      key: 'individualTrustees',
      label: 'Individual Trustees',
    },
    {
      key: 'corporateTrusteeDirectors',
      label: 'Corporate Trustee Directors',
    },
    {
      key: 'individualGuarantors',
      label: 'Individual Guarantors',
    },
    {
      key: 'companyGuarantors',
      label: 'Company Guarantors',
    },
    {
      key: 'asset',
      label: 'Asset',
    },
    {
      key: 'additionalAssets',
      label: 'Additional assets',
    },
  ];

  const result: OptionGroup<string>[] = [];

  fields.forEach(({ key, label }) => {
    if (data[key]) {
      const options: Option<string>[] = Array.isArray(data[key])
        ? data[key].map((obj: Record<any, any>) => ({
            label: obj.docsName,
            value: obj.id as string,
          }))
        : [
            {
              label: data[key].docsName,
              value: data[key].id as string,
            },
          ];

      result.push({
        label,
        options,
      });
    }
  });

  return result;
};

export const DocsTab: FC<DocsTabProps> = ({
  includes = EMPTY_ARR,
  documentTypeCategory = [CategoryParent.DocumentType],
}) => {
  const form = useForm();
  const { uuid } = useRequiredParams(['uuid']);
  const { definition } = useDefinition();
  const { can } = useCan();
  const [selectedDocs, setSelectedDocs] = useState<Doc[]>([]);
  const [docNeedsToBeUploaded, setDocNeedsToBeUploaded] = useState<Doc>(
    {} as Doc,
  );
  const [docNeedsToBeRejected, setDocNeedsToBeRejected] = useState<Doc>(
    {} as Doc,
  );
  const docsRequestModalApi = useOpenClose();
  const docUploadModalApi = useOpenClose();
  const docCreateModalApi = useOpenClose();
  const docRejectedModalApi = useOpenClose();
  const docsRequestRequiredModalApi = useOpenClose();
  const { updateDocument } = useUpdateApplicationDoc('doc', uuid);
  const { deleteDoc } = useDeleteApplicationDoc(uuid, definition);
  const { downloadApplicationDocs } = useDownloadApplicationDocs();

  const { application: ownerApplication } = useApplication(
    uuid,
    { definition },
    ownerParams,
  );

  const documentsPerPage = 15;

  const { hooks } = useContext(ApplicationContext);
  const isDisabledDocStore = isDeniedHook(hooks, 'docStore');

  const docsTabParams = useMemo(
    () => ({
      ...createFilterParams(
        createSortFilter({ column: 'updated_at', direction: 'desc' }),
      ),
      per_page: String(documentsPerPage),
      ...buildIncludeParams(includes),
      ...(documentTypeCategory.length && {
        'documentTypeCategory[]': documentTypeCategory,
      }),
    }),
    [includes, documentTypeCategory],
  );

  const { docsTab, isSuccess: docsTabIsSuccess } = useApplicationDocsTab(
    uuid,
    definition,
    docsTabParams,
  );

  const { categories, isSuccess: categoriesIsSuccess } = useSelectCategories({
    categories: documentTypeCategory,
  });

  const docTypes = useMemo(
    () =>
      categories?.length ? categoryToDropdown(categories[0], true) : EMPTY_ARR,
    [categories],
  );

  const docTypeGroups = useMemo(
    () =>
      docTypes.length
        ? [...docTypes.map((type) => type.label), 'Uncategorized']
        : EMPTY_ARR,
    [docTypes],
  );

  const massUpdateStatus = useEventHandler(
    async (status: DocStatusEnum): Promise<void> => {
      await Promise.all(
        selectedDocs.map(async (selectedDoc) =>
          updateDocument({ documentId: selectedDoc.id, data: { status } }),
        ),
      );
      handleSuccess('Updated');
    },
  );

  const massDelete = useEventHandler(async (): Promise<void> => {
    await Promise.all(
      selectedDocs.map(async (selectedDoc) => deleteDoc(selectedDoc.id)),
    );
    setSelectedDocs(EMPTY_ARR);
    handleSuccess('Deleted');
  });

  const downloadAttachments = useEventHandler(async (): Promise<void> => {
    await downloadApplicationDocs({ uuid, definitionName: definition });
  });

  const documentOwnerOptions = useMemo(
    () =>
      ownerApplication
        ? formatDocumentOwnerOptions(ownerApplication)
        : EMPTY_ARR,
    [ownerApplication],
  );

  const docsTabContextValue = useMemo(
    () => ({
      docNeedsToBeRejected,
      docNeedsToBeUploaded,
      docTypes,
      documentOwnerOptions,
      selectedDocs,
      setDocNeedsToBeRejected,
      setDocNeedsToBeUploaded,
      setSelectedDocs,
      docCreateModalApi,
      docRejectedModalApi,
      docsRequestModalApi,
      docsRequestRequiredModalApi,
      docUploadModalApi,
      uuid,
      definition,
    }),
    [
      docNeedsToBeRejected,
      docNeedsToBeUploaded,
      docTypes,
      documentOwnerOptions,
      selectedDocs,
      setDocNeedsToBeRejected,
      setDocNeedsToBeUploaded,
      setSelectedDocs,
      docCreateModalApi,
      docRejectedModalApi,
      docsRequestModalApi,
      docsRequestRequiredModalApi,
      docUploadModalApi,
      uuid,
      definition,
    ],
  );

  if (!categoriesIsSuccess || !docsTabIsSuccess) {
    return <Spinner />;
  }

  return (
    <FormProvider {...form}>
      <DocsTabContext.Provider value={docsTabContextValue}>
        <DocsRequestModal documentTypeCategory={documentTypeCategory} />
        <DocUploadModal doc={docNeedsToBeUploaded} />
        <DocsRequestRequiredModal includes={includes} />
        <DocCreateModal documentTypeCategory={documentTypeCategory} />
        <DocRejectedModal doc={docNeedsToBeRejected} />

        <Stack gap={8}>
          <Flex justifyContent="flex-end">
            {can(Permissions.DocsDelete) && (
              <Button
                colorPalette="red"
                disabled={selectedDocs.length === 0}
                onClick={massDelete}
                size="md"
                variant="solid"
              >
                Delete
              </Button>
            )}

            {can(Permissions.DocsUpdate) && (
              <Menu.Root positioning={{ placement: 'bottom-end' }}>
                <Menu.Trigger asChild>
                  <ChakraButton
                    colorPalette="gray"
                    disabled={selectedDocs.length === 0}
                    ml={4}
                    size="md"
                    variant="outline"
                  >
                    Mark as
                    <CgChevronDown />
                  </ChakraButton>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content>
                      {Object.values(DocStatusEnum).map((status) => (
                        <Menu.Item
                          key={status}
                          onClick={() => massUpdateStatus(status)}
                          value={`mark-as-${status}`}
                        >
                          Mark as {status}
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            )}

            <Button
              colorPalette="gray"
              fontWeight="normal"
              ml={4}
              onClick={docsRequestRequiredModalApi.onOpen}
              size="md"
              variant="outline"
            >
              <CgMail />
              Document Reminder
            </Button>

            {can(Permissions.DocsCreate) && (
              <Button
                colorPalette="gray"
                fontWeight="normal"
                ml={4}
                onClick={docsRequestModalApi.onOpen}
                size="md"
                variant="outline"
              >
                <CgFileDocument />
                Request document
              </Button>
            )}

            {can(Permissions.DocsCreate) && (
              <Button
                colorPalette="gray"
                disabled={isDisabledDocStore}
                fontWeight="normal"
                ml={4}
                onClick={docCreateModalApi.onOpen}
                size="md"
                variant="outline"
              >
                <CgMathPlus />
                Add new
              </Button>
            )}

            {can(Permissions.DocsDownload) && (
              <Button
                colorPalette="gray"
                fontWeight="normal"
                ml={4}
                onClick={downloadAttachments}
                size="md"
                variant="outline"
              >
                <CgSoftwareDownload />
                Download all files
              </Button>
            )}
          </Flex>

          {docTypeGroups.map((type, i) => (
            <DocsTabTypeTable
              // eslint-disable-next-line react/no-array-index-key
              key={`${type}-${i}`}
              documentTypeCategory={documentTypeCategory[0]}
              includes={includes}
              perPage={documentsPerPage}
              type={type}
            />
          ))}
        </Stack>
      </DocsTabContext.Provider>
    </FormProvider>
  );
};
